/**
 * Camera capture for the onboarding flow.
 *
 * The canvas round-trip in `downscaleToJpeg` is doing more than saving
 * bandwidth, though it does that too and it matters: the homeowner's network
 * may be the very thing that is broken, and a 4MB phone photo over a limping
 * connection is a second failure on top of the first.
 *
 * It also strips EXIF, which is how we avoid storing the GPS coordinates of a
 * customer's home, and it converts HEIC to JPEG. iOS hands back HEIC from the
 * camera and OpenAI does not accept it.
 */

/** Longest edge of an uploaded photo, in pixels. Plenty for a vision check. */
const MAX_EDGE = 1280;

/** JPEG quality. High enough to read a cable label, low enough to stay small. */
const QUALITY = 0.8;

/** Open the device camera (or file picker) and resolve with the chosen image. */
export function pickImage(): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    // Hints mobile browsers to open the rear camera; ignored on desktop.
    input.setAttribute("capture", "environment");
    input.onchange = () => resolve(input.files?.[0] ?? null);
    input.click();
  });
}

/**
 * Shrink an image to `MAX_EDGE` and re-encode it as JPEG.
 *
 * Returns the original file unchanged if the browser cannot decode it, so a
 * capture never fails outright: the backend rejects what it cannot use, and a
 * clear rejection beats a silent no-op.
 */
export async function downscaleToJpeg(file: File): Promise<File> {
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);

    const context = canvas.getContext("2d");
    if (!context) {
      return file;
    }
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", QUALITY),
    );
    if (!blob) {
      return file;
    }
    return new File([blob], "photo.jpg", { type: "image/jpeg" });
  } catch {
    return file;
  }
}
